import { isReducedMotionPreferred } from "@helpers/reducedMotion";
import { styleWithVars } from "@lib/cssVars";

/** The table's motion values resolved for one element, for JS-driven motion. */
export interface TableMotion {
    fastMs: number;
    mediumMs: number;
    slowMs: number;
    easeOut: string;
    easeInOut: string;
    /** OS setting or `data-motion="reduced"`: no movement, fades only. */
    isReduced: boolean;
    /** `data-motion="none"` (durations are 0). */
    isOff: boolean;
}

const FALLBACK_EASING = "ease-out";

const toMs = (seconds: string | undefined) => {
    const value = Number.parseFloat(seconds ?? "");
    return Number.isFinite(value) ? value * 1000 : 0;
};

const splitEasings = (value: string) =>
    value.match(/cubic-bezier\([^)]*\)|steps\([^)]*\)|[a-z-]+/g) ?? [];

/**
 * Resolves `--dg-duration-*` and `--dg-ease-*` (which may be calc()
 * expressions or overridden on an ancestor) through a probe inside
 * `element`, so a retimed table (the playground's slow motion) retimes its
 * JS animations too. Read per gesture, not per frame.
 */
export const readTableMotion = (element: HTMLElement): TableMotion => {
    const probe = document.createElement("div");
    probe.style.transition = [
        "opacity var(--dg-duration-fast, var(--duration-fast)) var(--dg-ease-out, var(--ease-out))",
        "transform var(--dg-duration-medium, var(--duration-medium)) var(--dg-ease-in-out, var(--ease-in-out))",
        "color var(--dg-duration-slow, var(--duration-slow)) ease",
    ].join(", ");
    probe.hidden = true;
    element.append(probe);
    const { transitionDuration, transitionTimingFunction } =
        getComputedStyle(probe);
    probe.remove();
    const [fast, medium, slow] = transitionDuration.split(",");
    const [easeOut, easeInOut] = splitEasings(transitionTimingFunction);
    const fastMs = toMs(fast);
    const mediumMs = toMs(medium);
    const level = element.closest<HTMLElement>("[data-motion]")?.dataset.motion;
    return {
        fastMs,
        mediumMs,
        slowMs: toMs(slow),
        easeOut: easeOut ?? FALLBACK_EASING,
        easeInOut: easeInOut ?? FALLBACK_EASING,
        isReduced: level === "reduced" || isReducedMotionPreferred(),
        isOff: level === "none" || mediumMs === 0,
    };
};

/**
 * `data-motion` and timing for a part portaled out of the root (the drag
 * preview), which can't inherit either.
 */
export const getPortalMotionProps = (motion: TableMotion) => {
    let level: "none" | "reduced" | undefined;
    if (motion.isOff) {
        level = "none";
    } else if (motion.isReduced) {
        level = "reduced";
    }
    return {
        "data-motion": level,
        style: styleWithVars({
            "--dg-duration-fast": `${motion.fastMs}ms`,
            "--dg-duration-medium": `${motion.mediumMs}ms`,
            "--dg-duration-slow": `${motion.slowMs}ms`,
            "--dg-ease-out": motion.easeOut,
            "--dg-ease-in-out": motion.easeInOut,
        }),
    };
};

/** Whether movement (transform) animations should run. */
export const canAnimateMovement = (motion: TableMotion) =>
    !motion.isReduced && !motion.isOff;
